import cx from 'clsx';
import { isValidElement, type ReactElement, type ReactNode } from 'react';
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components';
import IconChevronRightOutlined from '@repo/monochrome-icons/icon-chevron-right-outlined';
import IconOpenlinkOutlined from '@repo/monochrome-icons/icon-openlink-outlined';
import IconPlusOutlined from '@repo/monochrome-icons/icon-plus-outlined';
import { SkeletonLoader } from '#components/skeleton-loader/skeleton-loader';
import { Spinner } from '#components/spinner/spinner';
import styles from './selector.module.css';

interface SelectorBaseProps extends AriaButtonProps {
  icon?: ReactElement;
  title?: ReactNode;
  subtitle?: string | ReactElement;
  trailingContent?: string | ReactElement;
  disclaimer?: ReactNode;
  isLoading?: boolean;
  variant?: 'classic' | 'add' | 'link';
  actionIcon?: ReactElement;
  showActionIcon?: boolean;
  showLoadingIcon?: boolean;
  isTrailingContentEmphasized?: boolean;
}

/**
 * A Selector must expose an accessible name (WCAG 4.1.2). The button's name is
 * derived from its visible text, so at least one of `title`, `subtitle`, or
 * `content` must be provided — or an explicit `aria-label` / `aria-labelledby`
 * when the Selector is icon-only. Pure loading skeletons (`isLoading`) render no
 * text by design and are therefore exempt.
 */
export type SelectorProps = SelectorBaseProps &
  (
    | { title: ReactNode }
    | { subtitle: string | ReactElement }
    | { trailingContent: string | ReactElement }
    | { 'aria-label': string }
    | { 'aria-labelledby': string }
    | { isLoading: true }
  );

/**
 * This component is an incomplete implementation of the Qonto Selector component.
 * Though it adheres to the Qonto Selector API, it is not a full implementation.
 * It was agreed in this Slack thread (https://qonto.slack.com/archives/C06UZ4JKVHU/p1732540595204029).
 */
export function Selector({
  isDisabled = false,
  isLoading = false,
  isPending = false,
  variant = 'classic',
  className,
  icon,
  title,
  subtitle,
  trailingContent,
  disclaimer,
  onPress,
  actionIcon,
  showActionIcon = true,
  showLoadingIcon = true,
  isTrailingContentEmphasized,
  ...props
}: SelectorProps): ReactElement {
  const renderActionIcon = (): ReactElement => {
    if (actionIcon) {
      return actionIcon;
    }

    if (isPending) {
      return <Spinner data-testid="selector-spinner" size="medium" />;
    }

    if (variant === 'add') return <IconPlusOutlined />;
    if (variant === 'link') return <IconOpenlinkOutlined />;
    return <IconChevronRightOutlined />;
  };

  return (
    <AriaButton
      aria-busy={isLoading}
      className={cx(
        styles.selector,
        (variant === 'classic' || variant === 'link') && styles.classic,
        variant === 'add' && styles.add,
        isDisabled && styles.disabled,
        isLoading && styles.loading,
        className
      )}
      isDisabled={isDisabled}
      onPress={onPress}
      type="button"
      {...props}
      data-test-selector
    >
      {isLoading ? (
        <>
          {title ? <span className="sr-only">{title}</span> : null}
          {showLoadingIcon ? <SkeletonLoader.Avatar className={styles.icon} /> : null}
          <div className={styles.header}>
            <SkeletonLoader.Line className="mb-8" data-testid="selector-skeleton" width="90px" />
            <SkeletonLoader.Line className="mb-8" width="156px" />
          </div>
          {showActionIcon ? (
            <div className={styles['action-icon']}>
              <IconChevronRightOutlined />
            </div>
          ) : null}
        </>
      ) : (
        <>
          {icon ? (
            <div aria-hidden="true" className={styles.icon} data-test-selector-icon>
              {icon}
            </div>
          ) : null}
          <div className={styles.header}>
            {title ? (
              <span className={cx('body-standard', styles.title)} data-test-selector-title>
                {title}
              </span>
            ) : null}
            {subtitle ? (
              <span className={cx('body-standard', styles.subtitle)} data-test-selector-subtitle>
                {subtitle}
              </span>
            ) : null}
            {disclaimer ? (
              <div className={styles.disclaimer} data-test-selector-disclaimer>
                {disclaimer}
              </div>
            ) : null}
          </div>
          {trailingContent ? (
            <div
              className={cx(
                styles.trailingContent,
                isTrailingContentEmphasized && styles.trailingContentEmphasized
              )}
              data-test-selector-content
            >
              {isValidElement(trailingContent) ? (
                trailingContent
              ) : (
                <span className="body-standard">{trailingContent}</span>
              )}
            </div>
          ) : null}
          {showActionIcon ? (
            <div
              className={styles['action-icon']}
              data-test-selector-action-icon
              data-testid="selector-action-icon"
            >
              {renderActionIcon()}
            </div>
          ) : null}
        </>
      )}
    </AriaButton>
  );
}
